/**
 * Tolérances et capabilité, pour une caractéristique.
 *
 * C'est le panneau qui transforme des cartes de contrôle en réponse à la question que
 * l'usine se pose vraiment : « est-ce que je livre ? ». Une carte dit si le procédé est
 * stable ; seul le Cpk dit s'il tient la cote.
 *
 * Trois partis pris d'affichage, tous destinés à empêcher une lecture fausse :
 *
 * 1. **L'origine de chaque borne est visible.** « Lu dans le fichier » et « saisi » ne
 *    se valent pas devant un auditeur, et la personne qui saisit doit voir ce qu'elle
 *    écrase.
 * 2. **Un indice absent est expliqué, jamais remplacé.** Cp n'existe pas sur une
 *    spécification unilatérale. Afficher « 0 » ou « — » sans le dire laisserait croire
 *    à un procédé catastrophique ou à une panne de calcul.
 * 3. **Les limites de contrôle ne sont pas des tolérances**, et le panneau le rappelle
 *    là où la confusion se produit — au moment de saisir.
 */

import { AlertTriangle, FileInput, Loader2, Pencil } from "lucide-react";
import { useEffect, useState } from "react";

import {
  CAPABILITY_LEVELS,
  type Capability,
  type Tolerance,
  type ToleranceInput,
} from "./mappingApi";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  cn,
  type Verdict,
} from "./ui/primitives";

function verdictOfIndex(value: number | null): Verdict {
  if (value === null || !Number.isFinite(value)) return "unknown";
  if (value >= CAPABILITY_LEVELS.good) return "good";
  if (value >= CAPABILITY_LEVELS.acceptable) return "warn";
  return "bad";
}

const TONE: Record<Verdict, string> = {
  good: "text-good",
  warn: "text-warn",
  bad: "text-bad",
  unknown: "text-faint",
};

/** Un indice, avec la raison de son absence quand il n'existe pas. */
function Indice({
  label,
  value,
  hint,
  absent,
}: {
  label: string;
  value: number | null;
  hint: string;
  absent?: string;
}) {
  const verdict = verdictOfIndex(value);

  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[0.68rem] font-medium tracking-wide text-faint uppercase">
        {label}
      </span>
      {value === null ? (
        <span
          className="text-[0.95rem] text-faint"
          title={absent ?? "Non calculable"}
        >
          —
        </span>
      ) : (
        <span
          className={cn(
            "font-mono text-[1.15rem] font-semibold",
            TONE[verdict],
          )}
        >
          {value.toFixed(2)}
        </span>
      )}
      <span className="text-[0.66rem] leading-tight text-faint">
        {value === null ? (absent ?? hint) : hint}
      </span>
    </div>
  );
}

function ChampBorne({
  label,
  value,
  origin,
  rationale,
  onChange,
  onValidate,
  disabled,
}: {
  label: string;
  value: string;
  origin?: string;
  rationale?: string;
  onChange: (v: string) => void;
  onValidate: () => void;
  disabled: boolean;
}) {
  return (
    <label className="flex min-w-0 flex-col gap-1">
      <span className="flex items-center gap-1.5 text-[0.7rem] font-medium text-muted">
        {label}
        {origin === "fichier" && (
          <FileInput
            className="h-3 w-3 text-faint"
            aria-label="lu du fichier"
          />
        )}
        {origin === "saisie" && (
          <Pencil className="h-3 w-3 text-accent" aria-label="saisi" />
        )}
      </span>
      <input
        inputMode="decimal"
        className={cn(
          "h-8 w-full rounded-md border border-field bg-surface-2 px-2",
          "font-mono text-[0.85rem] text-ink",
          "outline-none transition-colors",
          "hover:border-accent/50 focus:border-accent focus:ring-2 focus:ring-accent/25",
          "disabled:cursor-not-allowed disabled:text-faint",
        )}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") onValidate();
        }}
        disabled={disabled}
        placeholder="—"
      />
      <span
        className="truncate text-[0.64rem] text-faint"
        title={rationale ?? ""}
      >
        {rationale ?? "non renseignée"}
      </span>
    </label>
  );
}

function afficher(v: number | null | undefined): string {
  return v === null || v === undefined ? "" : String(v);
}

function lire(texte: string): number | null | undefined {
  const propre = texte.trim().replace(",", ".");
  if (propre === "") return null;
  const nombre = Number(propre);
  return Number.isFinite(nombre) ? nombre : undefined;
}

export function TolerancePanel({
  code,
  tolerance,
  capability,
  onChange,
  busy,
}: {
  code: string;
  tolerance: Tolerance | undefined;
  capability: Capability | null;
  onChange: (code: string, patch: ToleranceInput) => void;
  busy: boolean;
}) {
  const origins = tolerance?.origins ?? {};
  const rationales = tolerance?.rationales ?? {};
  const bilaterale = capability?.lsl != null && capability.usl != null;

  // Les trois bornes sont saisies **ensemble**, puis appliquées d'un geste.
  //
  // La première version envoyait chaque borne à la sortie du champ. Passer du mini au
  // maxi déclenchait donc une analyse, qui désactivait les champs le temps de l'aller-
  // retour : la saisie était interrompue au milieu. Un formulaire qui se recharge pendant
  // qu'on le remplit n'est pas utilisable, quelle que soit la vitesse du serveur.
  //
  // La clé `code` remet l'état à zéro quand on change de caractéristique — sans quoi les
  // bornes de la précédente resteraient affichées sur la suivante.
  const [brouillon, setBrouillon] = useState({
    lsl: afficher(tolerance?.lsl),
    usl: afficher(tolerance?.usl),
    target: afficher(tolerance?.target),
  });

  useEffect(() => {
    setBrouillon({
      lsl: afficher(tolerance?.lsl),
      usl: afficher(tolerance?.usl),
      target: afficher(tolerance?.target),
    });
  }, [code, tolerance?.lsl, tolerance?.usl, tolerance?.target]);

  const lues = {
    lsl: lire(brouillon.lsl),
    usl: lire(brouillon.usl),
    target: lire(brouillon.target),
  };
  const malForme = Object.values(lues).some((v) => v === undefined);
  const modifie =
    !malForme &&
    (lues.lsl !== (tolerance?.lsl ?? null) ||
      lues.usl !== (tolerance?.usl ?? null) ||
      lues.target !== (tolerance?.target ?? null));

  const appliquer = () => {
    if (!modifie || malForme) return;
    onChange(code, {
      lsl: lues.lsl ?? null,
      usl: lues.usl ?? null,
      target: lues.target ?? null,
    });
  };

  return (
    <Card>
      <CardHeader
        title="Tolérances et capabilité"
        hint={
          capability
            ? `Indices calculés sur ${capability.n_values} valeurs de la fenêtre — sigma intra-groupe par ${capability.sigma_method}`
            : "Saisir au moins une limite pour obtenir Cp, Cpk, Pp et Ppk"
        }
      />

      <div className="grid gap-3 px-4 pb-3 sm:grid-cols-3">
        <ChampBorne
          label="Limite inférieure"
          value={brouillon.lsl}
          origin={origins.lsl}
          rationale={rationales.lsl}
          disabled={busy}
          onChange={(v) => {
            setBrouillon((b) => ({ ...b, lsl: v }));
          }}
          onValidate={appliquer}
        />
        <ChampBorne
          label="Cible"
          value={brouillon.target}
          origin={origins.target}
          rationale={rationales.target}
          disabled={busy}
          onChange={(v) => {
            setBrouillon((b) => ({ ...b, target: v }));
          }}
          onValidate={appliquer}
        />
        <ChampBorne
          label="Limite supérieure"
          value={brouillon.usl}
          origin={origins.usl}
          rationale={rationales.usl}
          disabled={busy}
          onChange={(v) => {
            setBrouillon((b) => ({ ...b, usl: v }));
          }}
          onValidate={appliquer}
        />
      </div>

      <div className="flex items-center gap-3 px-4 pb-3">
        <Button
          variant="primary"
          size="md"
          onClick={appliquer}
          disabled={!modifie || busy}
        >
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            "Appliquer les tolérances"
          )}
        </Button>
        {malForme && (
          <span className="text-[0.76rem] text-bad">
            Une des valeurs n'est pas un nombre.
          </span>
        )}
        {!malForme && modifie && (
          <span className="text-[0.76rem] text-muted">
            Les indices seront recalculés sur la fenêtre courante.
          </span>
        )}
      </div>

      {tolerance?.inconsistency && (
        <div className="mx-4 mb-3 flex gap-2 rounded-md border border-bad/25 bg-bad-soft px-3 py-2 text-[0.78rem] leading-snug text-bad">
          <AlertTriangle className="mt-px h-4 w-4 shrink-0" />
          <span>
            {tolerance.inconsistency} Les indices ne sont pas calculés tant que
            les bornes se contredisent — un Cpk négatif n'expliquerait pas sa
            propre cause.
          </span>
        </div>
      )}

      {capability ? (
        <>
          <div className="grid gap-4 border-t border-line px-4 py-3 sm:grid-cols-4">
            <Indice
              label="Cp"
              value={capability.cp}
              hint="étalement, procédé stable"
              absent={
                bilaterale
                  ? "non calculable"
                  : "indéfini : une seule limite renseignée"
              }
            />
            <Indice
              label="Cpk"
              value={capability.cpk}
              hint="étalement et centrage"
            />
            <Indice
              label="Pp"
              value={capability.pp}
              hint="performance observée"
              absent={
                bilaterale
                  ? "non calculable"
                  : "indéfini : une seule limite renseignée"
              }
            />
            <Indice
              label="Ppk"
              value={capability.ppk}
              hint="performance et centrage"
            />
          </div>

          <div className="border-t border-line px-4 py-2.5 text-[0.72rem] leading-snug text-muted">
            <span className="font-medium text-ink">Cp/Cpk</span> supposent le
            procédé stable et utilisent le sigma estimé par la carte.{" "}
            <span className="font-medium text-ink">Pp/Ppk</span> décrivent ce
            qui a réellement été produit, écart-type global à l'appui. Un écart
            marqué entre les deux couples signale une instabilité — c'est un
            signal, pas une incohérence.
          </div>

          {capability.warnings.length > 0 && (
            <div className="border-t border-line px-4 py-2.5">
              {capability.warnings.map((w) => (
                <div
                  key={w}
                  className="flex gap-2 text-[0.74rem] leading-snug text-warn"
                >
                  <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
                  {w}
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="border-t border-line px-4 py-3 text-[0.78rem] leading-snug text-muted">
          Aucun indice n'est calculé sans limite de spécification, et aucune
          n'est devinée à partir des mesures : cela reviendrait à comparer le
          procédé à lui-même, donc à produire un Cpk toujours flatteur.
          <span className="mt-1 block text-faint">
            À ne pas confondre avec les limites de contrôle tracées sur la carte
            : celles-ci sont calculées à partir du procédé, les tolérances
            viennent du plan.
          </span>
          {tolerance && (
            <Badge tone="accent" className="mt-2">
              Bornes détectées dans le fichier
            </Badge>
          )}
        </div>
      )}
    </Card>
  );
}
